import * as React from 'react';
import moment from 'moment';
import { isNil, isEmpty, get, toNumber } from 'lodash';
import { useState, useMemo, useContext, useCallback } from 'react';
import { TTypedTFunction } from '@lib/useTypedTranslation';
import { useTranslation } from '@lib/useTypedTranslation';
import { useLocation } from 'react-router';

import Card from '../../../components/card/card';
import { NoSelectionOverlay } from '../../../components/card/noSelectionOverlay';
import { RoundedLabel } from '../../../components/labels/roundedLabel';
import { TabPanel } from '../../../components/tabs/tab-panel';
import { TabPanelHeader } from '../../../components/tabs/tab-panel-header';
import { Tab } from '../../../components/tabs/tab';
import { DeviceDetailsInformation } from './deviceDetailsInformation';
import { getLast30DayDeviceDischarge, getLast30DaysDateParams } from '../../../services/core/devicePerformance';
import { DeviceInfoContext } from '../../core/device';
import { useWorldRequest } from '../../../lib/useWorldRequest';
import { useProductsContext } from '../../../context/products';
import { productNames } from '../../app/productsWrapper';

import './styles.css';
import './legacyDetailsCard.css';
import { DeviceCharts } from './deviceCharts';
import { useFormatDeviceIdentifier } from '../../../lib/useFormatDeviceIdentifier';

export interface IProps {
  id: string,
  data: any,
  isLoading?: boolean
}

export function DeviceDetailsCard(props: IProps) {
  const ns = 'translation';
  const ns1 = 'performance';
  const ns2 = 'batteryEssentials';
  const ns3 = 'batteryEssentialsDeviceView';
  const { t } = useTranslation([ns, ns1, ns2, ns3]);
  const { timeZoneOffset } = useContext(DeviceInfoContext);
  const product = useProductsContext();
  const { data, id, isLoading } = props;
  const deviceIdentifier = useFormatDeviceIdentifier(data);

  const statusLabel = getStatusLabel(data, t);
  const productTitle = product[productNames.batteryEssentials].title;
  const header = t('NO_SELECTION_HEADER', { title: productTitle, ns: ns2 }) + " - " + t('DEVICE_other', { ns });

  const dates = useMemo(() => {
    return getLast30DaysDateParams(moment.utc(), timeZoneOffset);
  }, [timeZoneOffset]);

  const params = useMemo(() => {
    return getRequestParams(id, dates);
  }, [id, dates]);

  const chartDataFetcher = useCallback(() => {
    if (!isEmpty(data) && data !== '-') {
      return getLast30DayDeviceDischarge(params);
    }
  }, [data, params]);

  const {
    loading: requestLoading,
    data: chartData
  } = useWorldRequest(chartDataFetcher, { initialData: [] });

  const location = useLocation();
  const parameters = new URLSearchParams(location.search);
  const deviceNotFound = (data === '-' || isEmpty(data)) && parameters.has('id') && isLoading === false;

  return (
    <div className="details-card battery-device" data-id={get(data, 'id')}>
      <NoSelectionOverlay
        show={(id === '-' || deviceNotFound) && isLoading === false}
        icon={deviceNotFound ? null : "fas fa-mobile-alt"}
        header={deviceNotFound ? t('DEVICE_NOT_FOUND', { ns: 'translation' }) : header}
        noSelectionText={deviceNotFound ? t('DEVICE_NOT_FOUND_BODY', { ns: ns3 }) : [t('NO_SELECTION_BODY', { ns: ns3 })]}
        opaque={true}
      />
      <Card noPadding={true}>
        <TabPanel>
          <TabPanelHeader>
            <div className="details-tab-header">
              <span className="selection-header">
                {t("DEVICE_STATUS_HEADER", { ns })} {deviceIdentifier}
              </span>
              {statusLabel}
            </div>
          </TabPanelHeader>
          <Tab id="Information" title={t("INFORMATION", { ns })}>
            <DeviceDetailsInformation
              key={'details' + id}
              id={id}
              data={data}
            />
          </Tab>
          <Tab id='DischargeChart' title={t("DEVICE_DISCHARGE", { ns: ns3 })}>
            <DeviceCharts
              key={'details' + id}
              id={id}
              data={chartData}
              isLoading={requestLoading}
              timeZoneOffset={timeZoneOffset}
            />
          </Tab>
        </TabPanel>
      </Card>
    </div>
  );
}

function getStatusLabel(data: any, t: TTypedTFunction) {
  const ns = 'translation';
  const ns1 = 'batteryEssentialsDeviceView';

  if (data?.averageDischargeStatus === 'red') {
    return <RoundedLabel title={t("HIGH_DISCHARGE", { ns: ns1 })} icon='fas fa-times-circle' type='red' />;
  }
  if (data?.averageDischargeStatus === 'yellow') {
    return <RoundedLabel title={t("HIGH_DISCHARGE", { ns: ns1 })} icon='fas fa-exclamation-triangle' type='yellow' />;
  }

  return <RoundedLabel title={t("NO_PROBLEMS", { ns })} icon='fas fa-check' type='green' />;
}

interface IRequestParams {
  deviceId: string,
  to: number,
  from: number
}

function getRequestParams(deviceId: string, dates: { from: number, to: number }): IRequestParams {
  return { deviceId, ...dates };
}
